import Util from "@/utils/util";
import { openProtocol } from "@babybus/lib-protocol";

type ProtocolVersionMap = Record<string, number>;
interface BaseHeaderData {
  VerID?: number;
  OSType?: number;
  [key: string]: any;
}

// 3.3 协议能力受商城 SDK 版本约束，当前均要求 sdkVersion >= 1。
const buguProtocolVersionMap: ProtocolVersionMap = {
  "babybus://bugu/onSignInSuccess": 1,
  "babybus://bugu/onPaySuccess": 1,
  "babybus://bugu/shareMiniProgram": 1,
  "babybus://bugu/isHideH5TitleBar": 1,
  "babybus://bugu/toggleTitleBarShareMenu": 1,
};

let mallSdkVersionCache: number | null = null;

async function getMallSdkVersion(forceRefresh = false): Promise<number> {
  if (!forceRefresh && mallSdkVersionCache !== null) {
    return mallSdkVersionCache;
  }

  try {
    let res = await openProtocol(`babybus://bugu/getMallSdkVersion`);
    res = Util.deserialize(res);
    if (res.status !== 1) {
      throw new Error(`${res.message}`);
    }
    const sdkVersion = res?.status == 1 ? res.data : 0;
    mallSdkVersionCache = sdkVersion;
    console.debug("获取商城SDK版本成功", sdkVersion);
    return sdkVersion;
  } catch (error) {
    console.warn("获取商城 SDK 版本失败", error);
    mallSdkVersionCache = 0;
    return 0;
  }
}

async function openBuguProtocol(protocol: string, data?: Record<string, any>): Promise<any> {
  const requiredVersion = buguProtocolVersionMap[protocol];
  let currentVersion: number | null = null;

  if (typeof requiredVersion === "number") {
    currentVersion = await getMallSdkVersion();
    if (currentVersion < requiredVersion) {
      console.debug("[openBuguProtocol:unsupported]", {
        protocol,
        data,
        requiredVersion,
        currentVersion,
      });
      return {
        status: 0,
        message: "MallSdkVersionNotSupported",
        data: "",
      };
    }
  }

  try {
    const result = data ? await openProtocol(protocol, data) : await openProtocol(protocol);
    const parsedResult = Util.deserialize(result);

    if (parsedResult?.status === 404) {
      const protocolError = new Error(parsedResult?.message);
      throw protocolError;
    }

    console.debug("[openBuguProtocol:success]", {
      protocol,
      data,
      result,
    });
    return result;
  } catch (error) {
    const sdkVersion = currentVersion ?? (await getMallSdkVersion());
    const logPayload = {
      protocol,
      data,
      error,
      sdkVersion,
    };

    if (Number(sdkVersion) < 1) {
      console.debug("[openBuguProtocol:debug-non-sdk]", logPayload);
    } else {
      console.debug("[openBuguProtocol:error]", logPayload);
    }

    throw error;
  }
}

async function signInSuccess(): Promise<any> {
  try {
    const sdkVersion = await getMallSdkVersion();
    const sdkNum = Number(sdkVersion);
    if (sdkNum >= 1) {
      return openBuguProtocol("babybus://bugu/onSignInSuccess");
    }

    return openProtocol("babybus://bugu/mallSignInSuccess");
  } catch (error) {
    console.warn("signInSuccess sdkVersion 获取失败，走兜底协议", error);
    return openProtocol("babybus://bugu/mallSignInSuccess");
  }
}

async function onPaySuccess(data: { channel: string; goods: { goodsID: string; goodsPrice: string }[] }): Promise<any> {
  return openBuguProtocol("babybus://bugu/paySuccess", data);
}

async function paySuccess(data: { channel: string; goods: { goodsID: string; goodsPrice: string }[] }): Promise<any> {
  try {
    const sdkVersion = await getMallSdkVersion();
    const sdkNum = Number(sdkVersion);
    if (sdkNum >= 1) {
      return onPaySuccess(data);
    }

    return openProtocol("babybus://bugu/paySuccess", data);
  } catch (error) {
    return openProtocol("babybus://bugu/paySuccess", data);
  }
}

async function shareMiniProgram(data: Record<string, any>): Promise<any> {
  const sdkVersion = await getMallSdkVersion();
  if (Number(sdkVersion) >= 1) {
    return openBuguProtocol("babybus://bugu/shareMiniProgram", data);
  }

  let baseHeaderInfo: { data?: BaseHeaderData } = { data: {} };
  try {
    baseHeaderInfo = Util.deserialize(
      await openProtocol(`babybus://account/getBaseHeadInfo`, {
        headKeys: ["VerID", "OSType"],
      }),
    );
  } catch (error) {
    console.warn("获取分享兼容信息失败，使用通用分享协议", error);
  }

  const { VerID = 0, OSType = 0 } = baseHeaderInfo?.data || {};
  if (VerID >= 4900 && OSType == 1) {
    // 旧版 App 支持同名协议但没有商城 SDK，不能使用 openBuguProtocol 拦截。
    return openProtocol("babybus://bugu/shareMiniProgram", data);
  }
  // 仅元信息读取失败时兜底，实际分享失败交由调用方处理，避免重复弹起分享。
  return openProtocol("babybus://util/share", data);
}

async function showTitleBarShareMenu(show: boolean): Promise<any> {
  return openBuguProtocol("babybus://bugu/toggleTitleBarShareMenu", {
    url: window.location.href,
    show,
  });
}

/**
 * 关闭web-view
 */
function closeBrowser(): void {
  try {
    console.debug("---进入方法closeWebClick---");
    openProtocol(`babybus://util/closeWebClick`);
  } catch (error) {
    console.error(error);
  }
}

/**
 * 检测终端是否安装了某个app
 * @param {Array} appNameArr app列表
 * @return {Object} 被检测的app键值对结果 可选，iOS不返回值
 */
async function checkAppIsInstalled(appNameArr: string | string[]): Promise<Record<string, any>> {
  appNameArr = typeof appNameArr === "string" ? [appNameArr] : appNameArr;

  try {
    let res = await openProtocol(`babybus://apk/isAppInstalled`, {
      appKeys: appNameArr,
    });
    res = Util.deserialize(res);
    const data = res.status == 1 ? Util.deserialize(res.data) : {};
    return data;
  } catch (error) {
    console.error(error);
    return {};
  }
}

/**
 * 唤起客户端登录
 * @param {String} jsonStrEncode 支付数据
 */
function pay(jsonStrEncode: string): void {
  console.debug("---调用客户端支付---");
  const protocol = `?Client=Vip&Type=BuyGoods&Orderbean=${jsonStrEncode}&CallbackFun=PayCallback`;
  window.location.href = protocol;
}

/**
 * 唤起客户端登录
 */
async function toLogin(): Promise<boolean> {
  console.debug("---调用babybus://account/showLogin---", window.location.href);
  try {
    let res = await openProtocol(`babybus://account/showLogin`);
    res = Util.deserialize(res);
    const loginStatue = res.status == 1 ? true : false;
    return loginStatue;
  } catch (error) {
    console.error(error);
    return false;
  }
}

/**
 * 获取客户端登录状态
 * @return {Promise}
 * 注意：返回的是promise，如需获取状态，移步至store获取（在main.js中对store设置了状态）
 */
async function isLogin(): Promise<boolean> {
  try {
    console.debug("---调用babybus://account/isLogin---");
    let res = await openProtocol(`babybus://account/isLogin`);
    res = Util.deserialize(res);
    const isLogin = res.status == 1 ? !!Util.deserialize(res.data) : false;
    return isLogin;
  } catch (error) {
    console.error(error);
    return false;
  }
}

/**
 * 获取客户端vip状态
 * @return {Promise}
 * 注意：返回的是promise，如需获取状态，移步至store获取（在main.js中对store设置了状态）
 */
async function isVip(): Promise<boolean> {
  try {
    console.debug("---调用babybus://account/getUserInfoForWeb/vip---");
    let res = await openProtocol(`babybus://account/getUserInfoForWeb`);

    res = Util.deserialize(res);
    const data = res.status == 1 ? Util.deserialize(res.data) : {};
    const isVip = Number(data?.isVip) == 1;
    return isVip;
  } catch (error) {
    console.error(error);
    return false;
  }
}

/**
 * 获取客户端vip状态
 * @return {Promise}
 * 注意：返回的是promise，如需获取状态，移步至store获取（在main.js中对store设置了状态）
 */
async function getUserInfoForWeb(): Promise<Record<string, any>> {
  try {
    console.debug("---调用babybus://account/getUserInfoForWeb---");
    let res = await openProtocol(`babybus://account/getUserInfoForWeb`);
    res = Util.deserialize(res);
    const UserInfoForWeb = res.status == 1 ? Util.deserialize(res.data) : {};
    return UserInfoForWeb;
  } catch (error) {
    console.error(error);
    return {};
  }
}

/**
 * 获取客户端头部信息
 * @return {Promise}
 * 注意：返回的是promise，如需获取状态，移步至store获取（在main.js中对store设置了状态）
 */
async function getHeadInfo(): Promise<Record<string, any>> {
  try {
    console.debug("---调用babybus://account/getHeadInfo---");
    let res = await openProtocol(`babybus://account/getHeadInfo`);
    console.debug("babybus://account/getHeadInfo返回信息：", res);
    res = Util.deserialize(res);
    const headInfo = res.status == 1 ? Util.deserialize(res.data) : {};
    return headInfo;
  } catch (error) {
    console.error(error);
    return {};
  }
}

/**
 * 获取客户端头部信息
 * @return {Promise}
 * 注意：返回的是promise，如需获取状态，移步至store获取（在main.js中对store设置了状态）
 */
async function saveImageToAlbum(url: string): Promise<any> {
  try {
    console.debug("---调用babybus://util/saveImageToAlbum---");
    const imgUrl = encodeURIComponent(url);
    const res = await openProtocol(`babybus://util/saveImageToAlbum`, {
      url: `${imgUrl}`,
    });
    return res;
  } catch (error) {
    console.error(error);
  }
}

/**
 * 刷新用户信息
 * @return {Promise}
 */
async function refreshUserInfo(): Promise<any> {
  try {
    console.debug("---调用babybus://account/refreshUserInfo---");
    let res = await openProtocol(`babybus://account/refreshUserInfo`);
    return res;
  } catch (error) {
    console.error(error);
  }
}

interface OpenWebViewOptions {
  title?: string; // 窗口标题
  external?: number; // 是否打开外部浏览器; 0: 否（默认）；1: 是
  type?: number; // 横竖屏类型；1: 竖屏，2: 横屏（默认竖屏）
  hasTitle?: number; // 是否有标题栏；0: 没有；1: 有（默认）
  hasHeader?: number; // 是否在url里带头部信息; 0: 不带（默认）；1: 带
  hasBottomNavBar?: number; // 是否展示底部导航栏（iOS支持）；0: 不展示；1: 展示（默认）
  parentCheckType?: string; // 家长验证类型
  parentCheckTitle?: string; // 家长验证弹窗标题
  canPullRefresh?: number; // 是否允许下拉刷新；0: 不允许（默认）；1: 允许
}

function openWebView(url: string, data: OpenWebViewOptions = {}): void {
  //ios不传canPullRefresh会触发下拉刷新，这边强制写死未传时不允许下拉刷新
  if (!data?.canPullRefresh) {
    data.canPullRefresh = 0;
  }
  try {
    console.debug("---调用babybus://util/openWebView---", url, data);
    openProtocol(`babybus://util/openWebView`, {
      url: url,
      ...data,
    });
  } catch (error) {
    console.error(error);
  }
}
/**
 * @param {string} platform - 平台，可选值："weixin", "wxmoments", "qq", "qqzone", "weibo", "wxmini"；多平台用逗号分隔
 * @param {string} type - 分享类型，可选值："link", "img", "miniprogram"
 * @param {string} [title] - 分享标题（可选）
 * @param {string} [text] - 分享描述（可选）
 * @param {string} image - 分享图片
 * @param {string} [url] - 分享地址（可选）
 * @param {string} [appletUserName] - 小程序原始 ID（可选）
 * @param {string} [appletID] - 小程序 ID（可选）
 * @param {string} [path] - 小程序路径（可选）
 * @param {string} [miniprogramType] - 小程序版本，可选值："release"（正式版）, "test"（开发版）, "preview"（体验版）
 */

function share(options: {
  platform: string;
  type: string;
  title?: string;
  text?: string;
  image: string;
  url?: string;
  appletUserName?: string;
  appletID?: string;
  path?: string;
  miniprogramType?: string;
}): void {
  try {
    console.log("---DDDD调用babybus://util/share---", options);
    openProtocol(`babybus://util/share`, options);
  } catch (error) {
    console.error(error);
  }
}

const AppProtocol = {
  buguProtocolVersionMap,
  getMallSdkVersion,
  openBuguProtocol,
  signInSuccess,
  onPaySuccess,
  paySuccess,
  shareMiniProgram,
  showTitleBarShareMenu,
  closeBrowser,
  checkAppIsInstalled,
  toLogin,
  isLogin,
  isVip,
  getUserInfoForWeb,
  getHeadInfo,
  pay,
  saveImageToAlbum,
  refreshUserInfo,
  openWebView,
  share,
};

export default AppProtocol;
